import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { getGatewayUpdateUserDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-update-user.form-default-values';
import { GatewayUpdateUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/inputs/gateway-update-user.model';
import { GatewayUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/users/models/outputs/gateway-user.model';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { UserUpdateFormController } from '@app/feature/user/user-update/user-update-form/user-update-form.controller';
import {
  UpdateUserContextProvider,
  useUserContext,
} from '@app/feature/user/user.context';
import { useCpmsBoUpdateUserMutation } from '@service/cpms-bo-user.service';

interface UserUpdateControllerProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const UserUpdateController = ({
  onSuccess,
  onCancel,
}: UserUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updateUserMutation] = useCpmsBoUpdateUserMutation();
  const user = useUserContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<{
    userId: GatewayUserModel['id'];
    user: GatewayUpdateUserModel;
  }> = (errors) => {
    Logger.error(errors);
    toast.error(t('$.user-update.error', { error: t('global.unknown') }));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<{
    userId: GatewayUserModel['id'];
    user: GatewayUpdateUserModel;
  }> = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );

    if (!sanitizedObject.user) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    const updateUser = {
      userId: fieldValues.userId,
      user: sanitizedObject.user,
    };

    updateUserMutation(updateUser)
      .unwrap()
      .then(() => {
        toast.success(t('$.user-update.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(t('$.user-update.error', { error: errorMessage }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getGatewayUpdateUserDefaultValues(user);

  return (
    <UpdateUserContextProvider data={data}>
      <UserUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateUserContextProvider>
  );
};
